/**
 * Meta Conversions API + Browser Pixel Tracking
 *
 * Fires browser (fbq) and server (WP meta-capi plugin) with the same event_id.
 * Meta event names match Everflow Partner → Meta postbacks (see metaEventNames.js).
 */

import { config } from '../config/env'
import { logger } from './logger'
import { createMetaEventId } from './metaEventId'
import { initializeMetaPixel, syncMetaAdvancedMatching, isMetaAllowedAffiliate } from './metaPixel'
import {
  META_EVENT_FIRST_PURCHASE,
  META_EVENT_PURCHASE,
  META_EVENT_QUIZ_COMPLETED,
  META_EVENT_START_QUIZ,
} from './metaEventNames'

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'))
  return match ? match[2] : null
}

const getFbclid = () => {
  try {
    return new URLSearchParams(window.location.search).get('fbclid')
  } catch (_) {
    return null
  }
}

/**
 * Click id for CAPI. The `_fbc` cookie only exists once the pixel has loaded on
 * a page carrying `fbclid`, so when it's missing we rebuild Meta's own format
 * (`fb.<subdomainIndex>.<creationTime>.<fbclid>`) from the URL. `fbclid` is kept
 * across funnel navigation by getPreservedParams (see urlParams.js).
 */
const getFbc = () => {
  const cookie = getCookie('_fbc')
  if (cookie) return cookie

  const fbclid = getFbclid()
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null
}

/**
 * Funnel stores DOB as YYYY-MM-DD, but some sources persist an ISO timestamp
 * (`1990-06-19T00:00:00.000Z`). Always send the date portion only.
 * Fall back to month/day/year parts when the combined field is missing.
 * Sent plaintext — WordPress should normalize to YYYYMMDD and SHA-256 hash
 * as Meta `db` before forwarding to CAPI.
 */
const getDateOfBirth = (funnelData = {}) => {
  const stored = String(funnelData.date_of_birth || '').trim()
  const isoDate = stored.match(/^(\d{4}-\d{2}-\d{2})/)
  if (isoDate) return isoDate[1]

  const year = String(funnelData.year || '').trim()
  const month = String(funnelData.month || '').trim().padStart(2, '0')
  const day = String(funnelData.day || '').trim().padStart(2, '0')
  if (year && month !== '00' && day !== '00') return `${year}-${month}-${day}`

  return null
}

const firstPresent = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue
    const trimmed = String(value).trim()
    if (trimmed) return trimmed
  }
  return null
}

const buildUserPayload = (funnelData) => {
  const user = {}
  if (funnelData.email)      user.email      = funnelData.email
  if (funnelData.phone)      user.phone      = funnelData.phone
  if (funnelData.first_name) user.first_name = funnelData.first_name
  if (funnelData.last_name)  user.last_name  = funnelData.last_name

  // Checkout writes shipping_* (and NAD upsell writes nad_shipping_*).
  // Unprefixed city/state/zip/country exist on some earlier steps only.
  const city = firstPresent(funnelData.city, funnelData.shipping_city, funnelData.nad_shipping_city)
  const state = firstPresent(funnelData.state, funnelData.shipping_state, funnelData.nad_shipping_state)
  const zip = firstPresent(
    funnelData.zip,
    funnelData.postal_code,
    funnelData.shipping_postal_code,
    funnelData.nad_shipping_postal_code,
  )
  const country = firstPresent(funnelData.country, funnelData.shipping_country, funnelData.nad_shipping_country)
  if (city)    user.city    = city
  if (state)   user.state   = state
  if (zip)     user.zip     = zip
  if (country) user.country = country

  if (funnelData.entry_id)   user.external_id = String(funnelData.entry_id)
  const dateOfBirth = getDateOfBirth(funnelData)
  if (dateOfBirth)           user.date_of_birth = dateOfBirth
  return user
}

const resolveProductClassification = (productName) => {
  const name = String(productName || '')
  if (name.includes('Semaglutide')) {
    return { type: 'GLP-1', medication: 'Semaglutide' }
  }
  if (name.includes('Tirzepatide')) {
    return { type: 'GIP_GLP-1', medication: 'Tirzepatide' }
  }
  if (name.includes('Sermorelin')) {
    return { type: 'GROWTH_HORMONE_RELEASING_PEPTIDE', medication: 'Sermorelin' }
  }
  if (name.includes('NAD+')) {
    return { type: 'CELLULAR_COENZYME', medication: 'NAD+' }
  }
  const hrtMarkers = ['Estradiol', 'Progesterone', 'IR Capsule', 'Estriol', 'DHEA']
  if (hrtMarkers.some((marker) => name.includes(marker))) {
    return { type: 'HORMONE_REPLACEMENT_THERAPY', medication: name }
  }
  return null
}

const buildProductPayload = (funnelData) => {
  const classification = resolveProductClassification(funnelData.selected_product_name)
  return {
    id:         funnelData.selected_product_id   || '',
    name:       funnelData.selected_product_name || '',
    price:      funnelData.selected_product_price || 0,
    type:       classification?.type || funnelData.selected_product_type || '',
    medication: classification?.medication || funnelData.selected_product_medication || '',
  }
}

const parseMetaValue = (price) => {
  if (typeof price === 'number' && !Number.isNaN(price)) return price
  if (typeof price === 'string') {
    const parsed = parseFloat(price.replace(/[$,]/g, ''))
    return Number.isNaN(parsed) ? 0 : parsed
  }
  return 0
}

const buildBasePayload = (funnelData, eventId) => ({
  event_id:         eventId,
  event_source_url: window.location.href,
  funnel_id:        funnelData.funnel_id || '',
  user:             buildUserPayload(funnelData),
  fbp:              getCookie('_fbp'),
  fbc:              getFbc(),
})

const buildFunnelStepCustomData = (funnelData) => {
  const customData = {
    content_type: 'product',
    currency:     'USD',
  }

  const funnelId = funnelData.funnel_id || ''
  if (funnelId) {
    customData.content_name = funnelId
  }

  const productId = funnelData.selected_product_id
  if (productId) {
    const id = String(productId)
    customData.content_ids = [id]
    customData.contents = [{ id, quantity: 1 }]
    customData.num_items = 1
  }

  const value = parseMetaValue(funnelData.selected_product_price)
  if (value > 0) {
    customData.value = value
  }

  return customData
}

const fireBrowserStandardEvent = (eventName, customData, eventId, funnelData) => {
  if (!window.fbq) {
    logger.debug(`[Meta] fbq not available, skipping browser ${eventName}`)
    return
  }
  try {
    if (funnelData) syncMetaAdvancedMatching(funnelData)
    window.fbq('track', eventName, customData, { eventID: eventId })
    logger.debug(`[Meta] Browser ${eventName} fired`, { eventId, customData })
  } catch (err) {
    logger.error(`[Meta] Browser ${eventName} failed:`, err)
  }
}

const fireBrowserCustomEvent = (eventName, customData, eventId, funnelData) => {
  if (!window.fbq) {
    logger.debug(`[Meta] fbq not available, skipping browser custom ${eventName}`)
    return
  }
  try {
    if (funnelData) syncMetaAdvancedMatching(funnelData)
    window.fbq('trackCustom', eventName, customData, { eventID: eventId })
    logger.debug(`[Meta] Browser custom ${eventName} fired`, { eventId })
  } catch (err) {
    logger.error(`[Meta] Browser custom ${eventName} failed:`, err)
  }
}

const fireServerEvent = async (endpoint, payload) => {
  try {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    const json = await res.json()
    if (!res.ok) {
      logger.warn(`[Meta CAPI] ${endpoint} server error:`, json)
    } else {
      logger.debug(`[Meta CAPI] ${endpoint} server success:`, json)
    }
  } catch (err) {
    logger.error(`[Meta CAPI] ${endpoint} fetch error:`, err)
  }
}

const shouldTrackMeta = (funnelData) => {
  if (config.metaPixelIds.length === 0) {
    logger.debug('[Meta] Skipping — no Meta Pixel ID configured')
    return false
  }
  if (!isMetaAllowedAffiliate(funnelData)) {
    logger.debug('[Meta] Skipping — affid not allowed for Meta tracking')
    return false
  }
  // App mount may run before affid is restored (e.g. resume links without
  // ?affid=), so load the pixel lazily for allowed sessions.
  if (!window.fbq) initializeMetaPixel(funnelData)
  return true
}

/**
 * Everflow postback: Start Quiz → Meta ViewContent
 * @param {Object} funnelData
 * @param {string} [metaEventId]
 */
export const trackMetaStartQuiz = (funnelData, metaEventId) => {
  if (!shouldTrackMeta(funnelData)) return

  const eventId = metaEventId || createMetaEventId()
  const customData = buildFunnelStepCustomData(funnelData)

  fireBrowserStandardEvent(META_EVENT_START_QUIZ, customData, eventId, funnelData)
  fireServerEvent('/meta-capi-start-quiz', buildBasePayload(funnelData, eventId))
}

/**
 * Everflow postback: Quiz Completed → Meta AddToCart
 * @param {Object} funnelData
 * @param {string} [metaEventId]
 */
export const trackMetaQuizCompleted = (funnelData, metaEventId) => {
  if (!shouldTrackMeta(funnelData)) return

  const eventId = metaEventId || createMetaEventId()
  const customData = buildFunnelStepCustomData(funnelData)

  fireBrowserStandardEvent(META_EVENT_QUIZ_COMPLETED, customData, eventId, funnelData)
  fireServerEvent('/meta-capi-quiz-completed', buildBasePayload(funnelData, eventId))
}

/**
 * Everflow postback: First Purchase → Meta SubscriptionCreation (main product only)
 * @param {Object} funnelData
 * @param {string} [metaEventId]
 */
export const trackMetaFirstPurchase = (funnelData, metaEventId) => {
  if (!shouldTrackMeta(funnelData)) return

  const eventId = metaEventId || createMetaEventId()
  const product = buildProductPayload(funnelData)
  const value = parseMetaValue(product.price)

  const customData = {
    content_ids:  product.id ? [product.id] : [],
    content_name: product.name,
    content_type: 'product',
    value,
    currency:     'USD',
  }

  fireBrowserCustomEvent(META_EVENT_FIRST_PURCHASE, customData, eventId, funnelData)
  fireServerEvent('/meta-capi-first-purchase', {
    ...buildBasePayload(funnelData, eventId),
    product,
  })
}

/**
 * Everflow postback: Purchase → Meta Purchase (main + upsell)
 * @param {Object} funnelData
 * @param {string} [metaEventId]
 */
export const trackMetaPurchase = (funnelData, metaEventId) => {
  if (!shouldTrackMeta(funnelData)) return

  const eventId = metaEventId || createMetaEventId()
  const product = buildProductPayload(funnelData)
  const value = parseMetaValue(product.price)

  const customData = {
    content_ids:  product.id ? [product.id] : [],
    content_name: product.name,
    content_type: 'product',
    value,
    currency:     'USD',
    contents:     product.id ? [{ id: product.id, quantity: 1 }] : [],
  }

  fireBrowserStandardEvent(META_EVENT_PURCHASE, customData, eventId, funnelData)
  fireServerEvent('/meta-capi-purchase', {
    ...buildBasePayload(funnelData, eventId),
    product,
  })
}

export default {
  trackMetaStartQuiz,
  trackMetaQuizCompleted,
  trackMetaFirstPurchase,
  trackMetaPurchase,
}
